import { DISCOVERY_POLICY_VERSION } from '../../packages/domain/src/47-learning-discovery.mjs';

export const FEED_DISCOVERY_VERSION = DISCOVERY_POLICY_VERSION;

export const FEED_PAGE_LIMITS = Object.freeze({ first: 3, next: 8 });

/** UI controller: one page in flight, one-page prefetch, stale generations discarded. */
export class FeedSession {
  /** Bind page reads to the current authoritative actor snapshot by identity.
   * The optional supplier must return the same object until an accepted snapshot
   * replaces it; omitted rows in a replacement remain unavailable to old reads.
   */
  constructor(fetchPage, { currentSnapshot = () => undefined } = {}) {
    this.fetchPage = fetchPage;
    this.currentSnapshot = currentSnapshot;
    this.pageSnapshots = new WeakMap();
    this.reset('automatic');
  }
  reset(mode) {
    this.generation = (this.generation ?? 0) + 1;
    this.mode = mode;
    this.items = [];
    this.cursor = undefined;
    this.started = false;
    this.cached = null;
    this.pending = null;
    this.meta = null;
  }
  /** Adopt only a newly authorized activation page; never a saved body preview. */
  adopt(page, mode = this.mode) {
    this.reset(mode);
    this.items = [...page.items];
    this.cursor = page.cursor;
    this.meta = page;
    this.started = true;
  }
  async prefetch() {
    if (this.cached) return this.cached;
    if (this.pending) return this.pending;
    if (this.started && !this.cursor) return null;
    const generation = this.generation;
    const cursor = this.cursor;
    const snapshot = this.currentSnapshot();
    const promise = this.fetchPage(this.mode, cursor, {
      limit: this.started ? FEED_PAGE_LIMITS.next : FEED_PAGE_LIMITS.first,
    })
      .then((page) => {
        if (generation === this.generation) {
          this.pageSnapshots.set(page, snapshot);
          this.cached = page;
        }
        return generation === this.generation ? page : null;
      })
      .finally(() => {
        if (generation === this.generation) this.pending = null;
      });
    this.pending = promise;
    return promise;
  }
  async next() {
    const generation = this.generation;
    let page = await this.prefetch();
    if (!page || generation !== this.generation || this.cached !== page) return [];
    this.cached = null;
    const current = this.currentSnapshot();
    const requested = this.pageSnapshots.get(page);
    this.pageSnapshots.delete(page);
    if (current && requested && current.me.id !== requested.me.id) return [];
    // A prefetch may precede a completed command. That command's complete
    // authorized snapshot owns current bodies and omissions; the page still
    // owns its frozen IDs, reasons and cursor. Never resurrect omitted rows or
    // overwrite newer reactions, comments or author projections with old DTOs.
    if (current && requested !== current) {
      const posts = new Map(current.posts.map((post) => [post.id, post]));
      page = {
        ...page,
        users: [],
        items: page.items
          .filter((item) => posts.has(item.id))
          .map((item) => ({ ...item, post: posts.get(item.id) })),
      };
    }
    const ids = new Set(this.items.map((item) => item.id));
    const added = page.items.filter((item) => !ids.has(item.id));
    this.items.push(...added);
    this.cursor = page.cursor;
    this.started = true;
    this.meta = page;
    return added;
  }
}
/** Touch gesture must begin at the top and outside an interactive/horizontal surface. */
export function pullDistance(start, touch, atTop) {
  if (!start || !atTop) return 0;
  const y = touch.y - start.y;
  return y > 0 && Math.abs(touch.x - start.x) < y * 0.7 ? Math.min(120, y) : 0;
}

/** Per-view reading continuity stores IDs, never post bodies. Paint must resolve each ID
 * through the current authorized snapshot. Actor/lane/order changes discard all memories. */
export class FeedContinuity {
  constructor() {
    this.scope = null;
    this.views = new Map();
  }
  align(
    actor,
    lane,
    order,
    learning,
    epoch,
    contentScope = 'ordinary',
    discoveryPolicyVersion = FEED_DISCOVERY_VERSION,
  ) {
    const scope = JSON.stringify([
      actor,
      lane,
      order,
      learning,
      epoch,
      contentScope,
      discoveryPolicyVersion,
    ]);
    if (scope !== this.scope) {
      this.scope = scope;
      this.views.clear();
    }
  }
  save(view, session, scroll) {
    if (!session.started) return;
    this.views.set(view, {
      items: session.items.map(({ id, reason, seen }) => ({
        id,
        reason,
        seen,
      })),
      cursor: session.cursor,
      meta: session.meta ? { ...session.meta, items: undefined, users: undefined } : null,
      scroll: Math.max(0, scroll),
    });
  }
  restore(view, session, order) {
    const saved = this.views.get(view);
    session.reset(order);
    if (!saved) return 0;
    session.items = saved.items.map((item) => ({ ...item }));
    session.cursor = saved.cursor;
    session.meta = saved.meta;
    session.started = true;
    return saved.scroll;
  }
}

/** Bounded transient alternate-surface orders. No post bodies survive warmup.
 * Every activation re-fetches the frozen first page through canonical authorization.
 * Context changes cancel pending work; expired orders fall back to a fresh feed.
 */
export class FeedWarmup {
  constructor(
    fetchPage,
    { now = Date.now, onPublicHints = () => {}, canWarm = () => true, ttlMs = 30000 } = {},
  ) {
    this.fetchPage = fetchPage;
    this.now = now;
    this.onPublicHints = onPublicHints;
    this.canWarm = canWarm;
    this.ttlMs = ttlMs;
    this.orders = new Map();
    this.controllers = new Set();
    this.generation = 0;
    this.context = null;
  }
  align(context) {
    const key = JSON.stringify([
      context.actor,
      context.mode,
      context.lane,
      context.contentScope ?? 'ordinary',
      context.discoveryPolicyVersion,
      context.learning,
      context.epoch,
    ]);
    if (key === this.key) return;
    this.clear();
    this.key = key;
    this.context = { ...context };
  }
  clear() {
    this.generation++;
    for (const controller of this.controllers) controller.abort();
    this.controllers.clear();
    this.orders.clear();
    this.pending = null;
    this.key = null;
    this.context = null;
  }
  valid(page, format) {
    return (
      page &&
      page.format === format &&
      page.scope === this.context?.lane &&
      (page.contentScope ?? 'ordinary') === (this.context?.contentScope ?? 'ordinary') &&
      (!this.context?.discoveryPolicyVersion ||
        page.discoveryPolicyVersion === this.context.discoveryPolicyVersion) &&
      Array.isArray(page.items) &&
      page.items.length <= 5 &&
      /^[a-f0-9]{48}$/.test(page.snapshot)
    );
  }
  async request(format, cursor) {
    if (!this.context?.actor || !['wall', 'visual', 'talk'].includes(format)) return null;
    const generation = this.generation;
    const controller = new AbortController();
    this.controllers.add(controller);
    try {
      const page = await this.fetchPage({
        ...this.context,
        format,
        cursor,
        limit: FEED_PAGE_LIMITS.first,
        signal: controller.signal,
      });
      if (generation !== this.generation || controller.signal.aborted) return null;
      if (!this.valid(page, format)) throw new Error('Feed response does not match this surface.');
      return page;
    } finally {
      this.controllers.delete(controller);
    }
  }
  warm(format) {
    if (!this.canWarm()) return Promise.resolve(null);
    const saved = this.orders.get(format);
    if (saved && this.now() - saved.createdAt < this.ttlMs) return Promise.resolve(saved);
    if (this.pending) return Promise.resolve(null);
    const generation = this.generation;
    const task = this.request(format)
      .then((page) => {
        if (!page || generation !== this.generation) return null;
        const saved = {
          snapshot: page.snapshot,
          ids: page.items.map(({ id }) => id),
          createdAt: this.now(),
        };
        this.orders.delete(format);
        while (this.orders.size >= 2) this.orders.delete(this.orders.keys().next().value);
        this.orders.set(format, saved);
        const urls = [
          ...new Set(
            page.items
              .flatMap(({ post }) => [
                post?.image,
                ...(post?.media ?? [])
                  .filter((media) => media.kind === 'image')
                  .map((media) => media.url),
              ])
              .filter(
                (url) =>
                  typeof url === 'string' &&
                  /^\/assets\/[a-zA-Z0-9/_-]+\.(?:webp|png|jpe?g|svg)$/.test(url),
              ),
          ),
        ].slice(0, 5);
        if (this.canWarm()) this.onPublicHints(urls);
        return saved;
      })
      .finally(() => {
        if (generation === this.generation) this.pending = null;
      });
    this.pending = task;
    return task;
  }
  async activate(format) {
    const generation = this.generation;
    const saved = this.orders.get(format);
    this.orders.delete(format);
    const cursor =
      saved && this.now() - saved.createdAt < this.ttlMs ? `${saved.snapshot}.0` : undefined;
    try {
      return await this.request(format, cursor);
    } catch (error) {
      if (generation !== this.generation || error.name === 'AbortError') return null;
      if ([400, 410].includes(error.status) && cursor) return this.request(format);
      throw error;
    }
  }
}

/** Reject stale server discovery policy/intent before a response enters UI caches.
 * Test adapters without a policy version retain their legacy response shape. */
export function validateDiscoveryResponse(response, contentScope, discoveryPolicyVersion) {
  if (
    discoveryPolicyVersion &&
    (response.discoveryPolicyVersion !== discoveryPolicyVersion ||
      response.contentScope !== contentScope)
  ) {
    throw Object.assign(new Error('This browsing order changed. Refresh to continue.'), {
      status: 410,
    });
  }
  return response;
}
